import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { reportSchedulesAPI } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import PdfBrandingSettings, { normalizeBranding } from './PdfBrandingSettings';
import PdfPreviewModal from './PdfPreviewModal';
import { Loader2, X, Plus, CalendarClock, Eye, Check, ChevronsUpDown, UserPlus } from 'lucide-react';
const TIMEZONES = [
    'America/New_York', 'America/Toronto', 'America/Montreal', 'America/Chicago',
    'America/Denver', 'America/Los_Angeles', 'America/Vancouver',
    'Europe/London', 'Europe/Paris', 'Europe/Madrid', 'UTC',
];

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const SECTION_KEYS = ['roi', 'platforms', 'employees', 'locations', 'timeline', 'topPerformers'];
const DEFAULT_SECTIONS = { roi: true, platforms: true, employees: true, locations: true, timeline: true, topPerformers: true };
// Section list + label namespace per schedulable report_key. Adding a report
// here is additive — `sales_report` keeps its exact existing behaviour.
const REPORT_SECTIONS = {
    sales_report: { keys: SECTION_KEYS, labelNs: 'salesReport.sections' },
    review_calculations_onepager: {
        keys: ['reviewsToNextTenth', 'roi', 'salesImpact', 'combinedOverview', 'perLocationBreakdown'],
        labelNs: 'salesCalc.sections',
    },
    // One-pager with a single ranked table — no optional sections to toggle.
    employee_taps: { keys: [], labelNs: 'reportSchedules.sections' },
};
// Report types offered by the selector. Kept deliberately narrow: Review
// Calculations still ships from its own screen only.
const SELECTABLE_REPORTS = [
    { key: 'sales_report', labelKey: 'reportSchedules.salesReportType', fallback: 'Sales Report' },
    { key: 'employee_taps', labelKey: 'reportSchedules.employeeTapsReport', fallback: 'Employee Tap Report' },
];
const sectionsFor = (key) => REPORT_SECTIONS[key] || REPORT_SECTIONS.sales_report;
const defaultSectionsFor = (key) =>
    sectionsFor(key).keys.reduce((acc, k) => ({ ...acc, [k]: true }), {});
const PERIODS = ['auto', 'yesterday', 'last_7_days', 'last_30_days', 'this_week', 'last_week', 'this_month', 'last_month', 'custom'];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const defaultTz = () => {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return TIMEZONES.includes(tz) ? tz : 'America/New_York';
    } catch {
        return 'America/New_York';
    }
};

const ScheduleReportModal = ({ open, onOpenChange, organizations, isSuper, inherited, editing, onSaved, reportKey = 'sales_report' }) => {
    const { t, i18n } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState('');
    const [frequency, setFrequency] = useState('weekly');
    const [runDate, setRunDate] = useState('');
    const [daysOfWeek, setDaysOfWeek] = useState(['mon']);
    const [dayOfMonth, setDayOfMonth] = useState(1);
    const [deliveryTime, setDeliveryTime] = useState('08:00');
    const [tz, setTz] = useState(defaultTz());
    const [recipients, setRecipients] = useState([]);
    const [recipientInput, setRecipientInput] = useState('');
    const [orgIds, setOrgIds] = useState([]);
    const [allOrgs, setAllOrgs] = useState(false);
    const [language, setLanguage] = useState('en');
    const [reportLanguageOverride, setReportLanguageOverride] = useState('preferred');
    // Full report configuration — sections + reporting period (independent
    // of schedule timing; dynamic presets resolve when the report runs)
    // The caller either PINS a report type (Review Calculations screen) or opts
    // into the selector; editing an existing schedule always pins its own type.
    const selectable = SELECTABLE_REPORTS.some((r) => r.key === reportKey);
    const [pickedReportKey, setPickedReportKey] = useState(reportKey);
    const activeReportKey = editing?.report_key || (selectable ? pickedReportKey : reportKey);
    const sectionDef = sectionsFor(activeReportKey);
    const [sections, setSections] = useState(DEFAULT_SECTIONS);
    const [period, setPeriod] = useState('auto');
    const [cfgStart, setCfgStart] = useState('');
    const [cfgEnd, setCfgEnd] = useState('');
    // Per-schedule branding: null/inherited until the user customizes it
    const [branding, setBranding] = useState(null);
    const [brandingOwned, setBrandingOwned] = useState(false);
    const [brandingLoading, setBrandingLoading] = useState(false);
    // Smart recipient selector — portal users grouped by restaurant
    const [userGroups, setUserGroups] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [userPickerOpen, setUserPickerOpen] = useState(false);
    const [showExternalInput, setShowExternalInput] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [preview, setPreview] = useState({ open: false, error: false, pages: [] });

    useEffect(() => {
        if (!open) {
            // Hard reset on close so a reopened modal can never flash the
            // previous schedule's branding/logo while fresh data loads.
            setBranding(null);
            setBrandingOwned(false);
            return;
        }
        if (editing) {
            setName(editing.name || '');
            setFrequency(editing.frequency || 'weekly');
            setRunDate(editing.run_date || '');
            setDaysOfWeek(editing.days_of_week?.length ? editing.days_of_week : ['mon']);
            setDayOfMonth(editing.day_of_month || 1);
            setDeliveryTime(editing.delivery_time || '08:00');
            setTz(editing.timezone || defaultTz());
            setRecipients([]);
            classifyRecipients(editing.recipients || []);
            setOrgIds(editing.organization_ids || []);
            setAllOrgs(!(editing.organization_ids || []).length);
            setLanguage(editing.language || 'en');
            setReportLanguageOverride(editing.report_language_override || 'preferred');
            setPickedReportKey(editing.report_key || reportKey);
            setSections({ ...defaultSectionsFor(editing.report_key || reportKey), ...(editing.config?.sections || {}) });
            setPeriod(editing.config?.period || (editing.frequency === 'one_time' ? 'custom' : 'auto'));
            setCfgStart(editing.config?.start_date || '');
            setCfgEnd(editing.config?.end_date || '');
            const own = editing.branding && Object.keys(editing.branding).length > 0;
            setBranding(own ? normalizeBranding(editing.branding) : null);
            setBrandingOwned(!!own);
        } else {
            setName('');
            setFrequency('weekly');
            setRunDate(new Date().toISOString().split('T')[0]);
            setDaysOfWeek(['mon']);
            setDayOfMonth(1);
            setDeliveryTime('08:00');
            setTz(defaultTz());
            setRecipients([]);
            const initial = inherited?.selectedOrgIds?.length
                ? inherited.selectedOrgIds
                : (inherited?.selectedOrg
                    ? [inherited.selectedOrg]
                    : (isSuper ? [] : organizations.map(o => o.id)));
            setOrgIds(initial);
            setAllOrgs(isSuper && !initial.length);
            setLanguage('en');
            setReportLanguageOverride('preferred');
            setPickedReportKey(reportKey);
            setSections({ ...defaultSectionsFor(reportKey), ...(inherited?.sections || {}) });
            setPeriod('auto');
            setCfgStart(inherited?.startDate || '');
            setCfgEnd(inherited?.endDate || '');
            setBranding(null);
            setBrandingOwned(false);
        }
        setRecipientInput('');
    }, [open, editing]); // eslint-disable-line react-hooks/exhaustive-deps

    // While the schedule has no branding of its own, mirror the restaurant's
    // saved report-level branding (live inheritance — same as send time).
    // Decides from the `editing` prop directly (not async state) and cancels
    // in-flight responses so a stale fetch can NEVER overwrite a schedule's
    // own saved branding/logo.
    const firstOrg = allOrgs ? null : (orgIds[0] || null);
    useEffect(() => {
        if (!open || brandingOwned) return undefined;
        if (editing?.branding && Object.keys(editing.branding).length > 0) return undefined;
        let cancelled = false;
        setBrandingLoading(true);
        reportSchedulesAPI.getBranding(firstOrg)
            .then(res => { if (!cancelled) setBranding(normalizeBranding(res.data?.branding || {})); })
            .catch(() => { if (!cancelled) setBranding(normalizeBranding({})); })
            .finally(() => { if (!cancelled) setBrandingLoading(false); });
        return () => { cancelled = true; };
    }, [open, brandingOwned, firstOrg, editing]);

    // Normalize stored recipients (strings or objects) and classify portal users.
    const classifyRecipients = async (raw) => {
        const out = [];
        for (const r of raw) {
            const email = typeof r === 'string' ? r : r.email;
            const storedLang = typeof r === 'string' ? 'auto' : (r.language || 'auto');
            try {
                const res = await reportSchedulesAPI.recipientInfo(email);
                const info = res.data;
                out.push(info.is_portal_user
                    ? { email, language: 'auto', is_portal: true, portal_language: info.preferred_language }
                    : { email, language: storedLang === 'auto' ? (info.saved_language || 'en') : storedLang, is_portal: false });
            } catch {
                out.push({ email, language: storedLang === 'auto' ? 'en' : storedLang, is_portal: false });
            }
        }
        setRecipients(out);
    };

    // Load portal users for the selected restaurants (selection aid only —
    // reuses existing user/org assignments; never auto-adds recipients).
    useEffect(() => {
        if (!open) return;
        setLoadingUsers(true);
        reportSchedulesAPI.portalUsers(allOrgs ? [] : orgIds)
            .then(res => setUserGroups(res.data?.groups || []))
            .catch(() => setUserGroups([]))
            .finally(() => setLoadingUsers(false));
    }, [open, allOrgs, JSON.stringify(orgIds)]); // eslint-disable-line react-hooks/exhaustive-deps

    const togglePortalUser = (u) => {
        setRecipients(prev => prev.some(r => r.email === u.email)
            ? prev.filter(r => r.email !== u.email)
            : [...prev, {
                email: u.email, language: 'auto', is_portal: true,
                portal_language: u.preferred_language, name: u.name,
            }]);
    };

    const addRecipient = async () => {
        const email = recipientInput.trim().toLowerCase();
        if (!email) return;
        if (!EMAIL_RE.test(email)) {
            toast.error(t('salesReport.schedule.invalidEmail'));
            return;
        }
        if (recipients.some(r => r.email === email)) { setRecipientInput(''); return; }
        try {
            const res = await reportSchedulesAPI.recipientInfo(email);
            const info = res.data;
            setRecipients(prev => [...prev, info.is_portal_user
                ? { email, language: 'auto', is_portal: true, portal_language: info.preferred_language }
                : { email, language: info.saved_language || 'en', is_portal: false }]);
        } catch {
            setRecipients(prev => [...prev, { email, language: 'en', is_portal: false }]);
        }
        setRecipientInput('');
    };

    const setRecipientLang = (email, lang) => {
        setRecipients(prev => prev.map(r => r.email === email ? { ...r, language: lang } : r));
    };

    const toggleDay = (d) => {
        setDaysOfWeek(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
    };

    const toggleOrg = (id) => {
        setAllOrgs(false);
        setOrgIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const sanitizeBranding = (b) => ({
        ...b,
        custom_logo_data: (b.header_logo === 'custom' || b.watermark === 'custom') ? b.custom_logo_data : null,
    });

    const buildPayload = () => ({
        name: name.trim() || 'Preview',
        report_key: activeReportKey,
        frequency,
        run_date: frequency === 'one_time' ? runDate : null,
        days_of_week: frequency === 'weekly' ? daysOfWeek : null,
        day_of_month: frequency === 'monthly' ? Number(dayOfMonth) : null,
        delivery_time: deliveryTime,
        timezone: tz,
        recipients: recipients.map(r => ({ email: r.email, language: r.language, is_portal: !!r.is_portal })),
        organization_ids: allOrgs ? [] : orgIds,
        language: ['en', 'fr', 'es'].includes((i18n.language || 'en').split('-')[0])
            ? (i18n.language || 'en').split('-')[0] : 'en',
        report_language_override: reportLanguageOverride === 'preferred' ? null : reportLanguageOverride,
        config: {
            report_type: editing?.config?.report_type || inherited?.reportType || 'custom',
            period: frequency === 'one_time' && period === 'auto' ? 'custom' : period,
            start_date: (period === 'custom' || frequency === 'one_time') ? (cfgStart || null) : null,
            end_date: (period === 'custom' || frequency === 'one_time') ? (cfgEnd || null) : null,
            sections,
        },
        // Empty branding = keep inheriting the restaurant's live defaults;
        // a customized schedule carries its own self-contained copy.
        branding: brandingOwned && branding ? sanitizeBranding(branding) : {},
    });

    const previewPdf = async () => {
        if (!allOrgs && !orgIds.length) { toast.error(t('salesReport.schedule.noRestaurants')); return; }
        setPreviewing(true);
        setPreview({ open: true, error: false, pages: [] });
        try {
            const payload = buildPayload();
            if (!payload.recipients.length) payload.recipients = [{ email: 'preview@example.com', language: 'en' }];
            const res = await reportSchedulesAPI.previewImages(payload);
            const pages = res.data?.pages || [];
            setPreview({ open: true, error: !pages.length, pages });
        } catch (error) {
            setPreview({ open: true, error: true, pages: [] });
        } finally {
            setPreviewing(false);
        }
    };

    const save = async () => {
        if (!name.trim()) { toast.error(t('salesReport.schedule.nameRequired')); return; }
        if (!recipients.length) { toast.error(t('salesReport.schedule.noRecipients')); return; }
        if (frequency === 'weekly' && !daysOfWeek.length) { toast.error(t('salesReport.schedule.noDays')); return; }
        if (!allOrgs && !orgIds.length) { toast.error(t('salesReport.schedule.noRestaurants')); return; }
        if (frequency === 'one_time' && (!cfgStart || !cfgEnd)) {
            toast.error(t('salesReport.schedule.periodRequired'));
            return;
        }
        if (brandingOwned && branding
            && (branding.header_logo === 'custom' || branding.watermark === 'custom')
            && !branding.custom_logo_data) {
            toast.error(t('salesReport.schedule.branding.logoMissing'));
            return;
        }
        const payload = buildPayload();
        payload.name = name.trim();
        setSaving(true);
        try {
            if (editing) {
                await reportSchedulesAPI.update(editing.id, payload);
                toast.success(t('salesReport.schedule.updated'));
            } else {
                await reportSchedulesAPI.create(payload);
                toast.success(t('salesReport.schedule.saved'));
            }
            onSaved?.();
            onOpenChange(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || t('salesReport.schedule.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const effPeriod = frequency === 'one_time' && period === 'auto' ? 'custom' : period;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="schedule-report-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CalendarClock className="w-5 h-5 text-purple-600" />
                        {editing ? t('salesReport.schedule.editTitle') : t('salesReport.schedule.title')}
                    </DialogTitle>
                    <DialogDescription>
                        {activeReportKey === 'sales_report'
                            ? t('salesReport.schedule.desc')
                            : t('reportSchedules.genericDesc', 'Automatically generate and email this report as a branded PDF on a recurring schedule. Each recipient receives it in their own language.')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    {/* Report type — which report this schedule sends. Locked
                        once a schedule exists, so an existing sales_report
                        schedule can never silently become another report. */}
                    {selectable && (
                        <div>
                            <Label className="mb-2 block">
                                {t('reportSchedules.reportType', 'Report type')}
                            </Label>
                            <Select
                                value={activeReportKey}
                                onValueChange={(v) => {
                                    setPickedReportKey(v);
                                    setSections(defaultSectionsFor(v));
                                }}
                                disabled={!!editing}
                            >
                                <SelectTrigger data-testid="schedule-report-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SELECTABLE_REPORTS.map((r) => (
                                        <SelectItem key={r.key} value={r.key}
                                            data-testid={`schedule-report-type-opt-${r.key}`}>
                                            {t(r.labelKey, r.fallback)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Name */}
                    <div>
                        <Label className="mb-2 block">{t('salesReport.schedule.name')}</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('salesReport.schedule.namePlaceholder')}
                            data-testid="schedule-name-input"
                        />
                    </div>

                    {/* Frequency — fixed widths that wrap; native time inputs never overlap neighbors */}
                    <div className="flex flex-wrap gap-x-8 gap-y-4">
                        <div className="w-44">
                            <Label className="mb-2 block">{t('salesReport.schedule.frequency')}</Label>
                            <Select value={frequency} onValueChange={setFrequency}>
                                <SelectTrigger data-testid="schedule-frequency-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="one_time">{t('salesReport.schedule.freq.one_time')}</SelectItem>
                                    <SelectItem value="daily">{t('salesReport.schedule.freq.daily')}</SelectItem>
                                    <SelectItem value="weekly">{t('salesReport.schedule.freq.weekly')}</SelectItem>
                                    <SelectItem value="monthly">{t('salesReport.schedule.freq.monthly')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="w-44">
                            <Label className="mb-2 block">{t('salesReport.schedule.time')}</Label>
                            <input
                                type="time"
                                value={deliveryTime}
                                onChange={(e) => setDeliveryTime(e.target.value)}
                                className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm h-10"
                                data-testid="schedule-time-input"
                            />
                        </div>
                        <div className="w-56">
                            <Label className="mb-2 block">{t('salesReport.schedule.timezone')}</Label>
                            <Select value={tz} onValueChange={setTz}>
                                <SelectTrigger data-testid="schedule-tz-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {TIMEZONES.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {frequency === 'one_time' && (
                        <div className="w-52">
                            <Label className="mb-2 block">{t('salesReport.schedule.runDate')}</Label>
                            <input
                                type="date"
                                value={runDate}
                                onChange={(e) => setRunDate(e.target.value)}
                                className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                                data-testid="schedule-run-date"
                            />
                        </div>
                    )}

                    {/* Reporting period — independent from schedule timing;
                        dynamic presets resolve when the report runs */}
                    <div className="flex flex-wrap gap-x-8 gap-y-4">
                        <div className="w-64">
                            <Label className="mb-2 block">{t('salesReport.schedule.period.label')}</Label>
                            <Select value={effPeriod} onValueChange={setPeriod}>
                                <SelectTrigger data-testid="schedule-period-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PERIODS.filter(p => !(p === 'auto' && frequency === 'one_time')).map(p => (
                                        <SelectItem key={p} value={p}>{t(`salesReport.schedule.period.${p}`)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {effPeriod === 'custom' && (
                            <>
                                <div className="w-52">
                                    <Label className="mb-2 block">{t('salesReport.schedule.periodStart')}</Label>
                                    <input
                                        type="date"
                                        value={cfgStart}
                                        onChange={(e) => setCfgStart(e.target.value)}
                                        className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                                        data-testid="schedule-period-start"
                                    />
                                </div>
                                <div className="w-52">
                                    <Label className="mb-2 block">{t('salesReport.schedule.periodEnd')}</Label>
                                    <input
                                        type="date"
                                        value={cfgEnd}
                                        onChange={(e) => setCfgEnd(e.target.value)}
                                        className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                                        data-testid="schedule-period-end"
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {frequency === 'weekly' && (
                        <div>
                            <Label className="mb-2 block">{t('salesReport.schedule.daysOfWeek')}</Label>
                            <div className="flex flex-wrap gap-2">
                                {DAYS.map(d => (
                                    <button
                                        key={d}
                                        type="button"
                                        onClick={() => toggleDay(d)}
                                        data-testid={`schedule-day-${d}`}
                                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                                            daysOfWeek.includes(d)
                                                ? 'bg-purple-600 text-white border-purple-600'
                                                : 'bg-background text-muted-foreground border-input hover:border-purple-400'
                                        }`}
                                    >
                                        {t(`salesReport.schedule.days.${d}`)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {frequency === 'monthly' && (
                        <div>
                            <Label className="mb-2 block">{t('salesReport.schedule.dayOfMonth')}</Label>
                            <Input
                                type="number"
                                min={1}
                                max={28}
                                value={dayOfMonth}
                                onChange={(e) => setDayOfMonth(e.target.value)}
                                className="max-w-[120px]"
                                data-testid="schedule-dom-input"
                            />
                        </div>
                    )}

                    <p className="text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2" data-testid="schedule-period-hint">
                        {effPeriod === 'auto'
                            ? t(`salesReport.schedule.periodHint.${frequency}`, {
                                start: cfgStart || inherited?.startDate, end: cfgEnd || inherited?.endDate,
                            })
                            : effPeriod === 'custom'
                                ? t('salesReport.schedule.period.customHint', { start: cfgStart || '—', end: cfgEnd || '—' })
                                : t('salesReport.schedule.period.dynamicHint', { period: t(`salesReport.schedule.period.${effPeriod}`) })}
                    </p>

                    {/* Recipients — restaurant-aware portal user picker + external emails */}
                    <div>
                        <Label className="mb-2 block">{t('salesReport.schedule.recipients')}</Label>
                        <Popover open={userPickerOpen} onOpenChange={setUserPickerOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full justify-between font-normal text-muted-foreground"
                                    data-testid="portal-user-picker-btn"
                                >
                                    <span className="flex items-center gap-2">
                                        <UserPlus className="w-4 h-4" />
                                        {t('salesReport.schedule.portalUsers.select')}
                                    </span>
                                    {loadingUsers
                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                        : <ChevronsUpDown className="w-4 h-4 opacity-50" />}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[360px] p-0" align="start">
                                <Command>
                                    <CommandInput
                                        placeholder={t('salesReport.schedule.portalUsers.search')}
                                        data-testid="portal-user-search"
                                    />
                                    <CommandList className="max-h-64">
                                        <CommandEmpty>{t('salesReport.schedule.portalUsers.none')}</CommandEmpty>
                                        {userGroups.map(g => (
                                            <CommandGroup
                                                key={g.organization_id || 'other'}
                                                heading={g.organization_name
                                                    ? (g.is_current
                                                        ? `${g.organization_name} · ${t('salesReport.schedule.portalUsers.currentBadge')}`
                                                        : g.organization_name)
                                                    : t('salesReport.schedule.portalUsers.otherGroup')}
                                            >
                                                {g.users.map(u => {
                                                    const selected = recipients.some(r => r.email === u.email);
                                                    return (
                                                        <CommandItem
                                                            key={`${g.organization_id}-${u.email}`}
                                                            value={`${u.name} ${u.email}`}
                                                            onSelect={() => togglePortalUser(u)}
                                                            data-testid={`portal-user-option-${u.email}`}
                                                        >
                                                            <Check className={`w-4 h-4 mr-2 shrink-0 text-emerald-600 ${selected ? 'opacity-100' : 'opacity-0'}`} />
                                                            <div className="min-w-0">
                                                                <div className="text-sm truncate">{u.name}</div>
                                                                <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                                                            </div>
                                                        </CommandItem>
                                                    );
                                                })}
                                            </CommandGroup>
                                        ))}
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                        {!showExternalInput ? (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowExternalInput(true)}
                                className="mt-2 text-muted-foreground"
                                data-testid="schedule-add-external-toggle"
                            >
                                <Plus className="w-3.5 h-3.5 mr-1" />
                                {t('salesReport.schedule.portalUsers.addExternal')}
                            </Button>
                        ) : (
                            <div className="flex gap-2 mt-2">
                                <Input
                                    value={recipientInput}
                                    onChange={(e) => setRecipientInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
                                    placeholder={t('salesReport.schedule.recipientPlaceholder')}
                                    data-testid="schedule-recipient-input"
                                    data-insight-id="schedule-recipient-input"
                                />
                                <Button type="button" variant="outline" onClick={addRecipient} data-testid="schedule-add-recipient-btn">
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-1.5">{t('salesReport.schedule.recipLangHint')}</p>
                        {recipients.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {recipients.map(r => (
                                    <div
                                        key={r.email}
                                        className="flex items-center gap-1.5 bg-secondary rounded-full pl-3 pr-1 py-1 text-xs"
                                        data-testid={`recipient-chip-${r.email}`}
                                        title={r.email}
                                    >
                                        <span>{r.name || r.email}</span>
                                        {r.is_portal ? (
                                            <span
                                                className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 font-medium"
                                                title={t('salesReport.schedule.recipPortalHint')}
                                                data-testid={`recipient-portal-${r.email}`}
                                            >
                                                {t('salesReport.schedule.recipPortal')} · {(r.portal_language || 'en').toUpperCase()}
                                            </span>
                                        ) : (
                                            <select
                                                value={r.language}
                                                onChange={(e) => setRecipientLang(r.email, e.target.value)}
                                                className="text-[10px] border border-input rounded px-1 py-0.5 bg-background cursor-pointer"
                                                title={t('salesReport.schedule.recipLangLabel')}
                                                data-testid={`recipient-lang-${r.email}`}
                                            >
                                                <option value="en">EN</option>
                                                <option value="fr">FR</option>
                                                <option value="es">ES</option>
                                            </select>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => setRecipients(recipients.filter(x => x.email !== r.email))}
                                            className="hover:text-red-500 ml-0.5 p-0.5"
                                            data-testid={`remove-recipient-${r.email}`}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Restaurants */}
                    <div>
                        <Label className="mb-2 block">{t('salesReport.schedule.restaurants')}</Label>
                        <p className="text-xs text-muted-foreground mb-2">{t('salesReport.schedule.restaurantsHint')}</p>
                        {isSuper && (
                            <div className="flex items-center gap-2 mb-2">
                                <Checkbox
                                    id="all-orgs"
                                    checked={allOrgs}
                                    onCheckedChange={(checked) => { setAllOrgs(!!checked); if (checked) setOrgIds([]); }}
                                    data-testid="schedule-all-orgs-checkbox"
                                />
                                <Label htmlFor="all-orgs" className="cursor-pointer font-medium">
                                    {t('salesReport.schedule.allRestaurants')}
                                </Label>
                            </div>
                        )}
                        {!allOrgs && (
                            <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-1">
                                {organizations.map(org => (
                                    <div key={org.id} className="flex items-center gap-2 py-1">
                                        <Checkbox
                                            id={`org-${org.id}`}
                                            checked={orgIds.includes(org.id)}
                                            onCheckedChange={() => toggleOrg(org.id)}
                                            data-testid={`schedule-org-checkbox-${org.id}`}
                                        />
                                        <Label htmlFor={`org-${org.id}`} className="cursor-pointer text-sm">{org.name}</Label>
                                    </div>
                                ))}
                                {!organizations.length && (
                                    <p className="text-xs text-muted-foreground p-1">—</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Included report sections — reports with a single fixed
                        layout (e.g. Employee Taps) declare no section keys. */}
                    {sectionDef.keys.length > 0 && (
                    <div>
                        <Label className="mb-2 block">{t('salesReport.schedule.sectionsTitle')}</Label>
                        <div className="grid sm:grid-cols-2 gap-2">
                            {sectionDef.keys.map(k => (
                                <div key={k} className="flex items-center gap-2">
                                    <Checkbox
                                        id={`sec-${k}`}
                                        checked={sections[k] !== false}
                                        onCheckedChange={(c) => setSections(prev => ({ ...prev, [k]: !!c }))}
                                        data-testid={`schedule-section-${k}`}
                                    />
                                    <Label htmlFor={`sec-${k}`} className="cursor-pointer text-sm">
                                        {t(`${sectionDef.labelNs}.${k}`)}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    </div>
                    )}

                    {/* Report Language — fixed language overrides recipient preferences */}
                    <div>
                        <Label className="mb-2 block">{t('salesReport.reportLanguage.label')}</Label>
                        <Select value={reportLanguageOverride} onValueChange={setReportLanguageOverride}>
                            <SelectTrigger data-testid="schedule-report-language-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="preferred">{t('salesReport.reportLanguage.preferred')}</SelectItem>
                                <SelectItem value="en">English</SelectItem>
                                <SelectItem value="fr">Français</SelectItem>
                                <SelectItem value="es">Español</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">{t('salesReport.reportLanguage.hint')}</p>
                    </div>

                    {/* PDF Branding — inherited from the restaurant until customized,
                        then owned by this schedule (independent of other schedules) */}
                    <div>
                        <PdfBrandingSettings
                            value={branding || {}}
                            onChange={(next) => { setBranding(next); setBrandingOwned(true); }}
                            loading={brandingLoading}
                        />
                        <div className="flex items-start justify-between gap-3 mt-1.5">
                            <p className="text-xs text-muted-foreground" data-testid="schedule-branding-mode-hint">
                                {brandingOwned
                                    ? t('salesReport.schedule.branding.ownHint')
                                    : t('salesReport.schedule.branding.inheritedHint')}
                            </p>
                            {brandingOwned && (
                                <button
                                    type="button"
                                    onClick={() => { setBrandingOwned(false); setBranding(null); }}
                                    className="text-xs text-purple-600 hover:underline underline-offset-2 shrink-0"
                                    data-testid="schedule-branding-reset-btn"
                                >
                                    {t('salesReport.schedule.branding.resetToDefault')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter className="gap-2 sm:justify-between">
                    <Button variant="secondary" onClick={previewPdf} disabled={previewing} data-testid="schedule-preview-btn" data-insight-id="schedule-preview-btn">
                        {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                        {t('salesReport.schedule.branding.preview')}
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="schedule-cancel-btn">
                            {t('salesReport.schedule.cancel')}
                        </Button>
                        <Button onClick={save} disabled={saving} data-testid="schedule-save-btn">
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {t('salesReport.schedule.save')}
                        </Button>
                    </div>
                </DialogFooter>

                <PdfPreviewModal
                    open={preview.open}
                    onOpenChange={(o) => setPreview(p => ({ ...p, open: o }))}
                    loading={previewing}
                    error={preview.error}
                    pages={preview.pages}
                    onRetry={previewPdf}
                    title={t('salesReport.pdfPreview.title')}
                />
            </DialogContent>
        </Dialog>
    );
};

export default ScheduleReportModal;
